// Saving a map area for use without signal. Saved tiles go in their own cache; the service worker serves them
// when there is no signal. Only the National Land Survey map is saved (open data, CC BY 4.0); OpenStreetMap forbids it.
import { $, el, toast, twoTap } from "../dom.ts";
import { fmtNum, t } from "../i18n.ts";
import { onRender } from "../state.ts";
import { bigMap, setBaseLayer } from "./bigmap.ts";
import { LAYERS, depthLayers, depthOn, layerName } from "./layers.ts";

const AREA_CACHE = "hfl-area-v1", MAX_AREA_TILES = 3000;
/** Rough size of one saved tile, MB (for the "up to about N MB" estimate). */
const MB_PER_TILE = 0.012;

function tileRange(map: L.Map, bounds: L.LatLngBounds, z: number, tileSize: number) {
  const a = map.project(bounds.getNorthWest(), z).divideBy(tileSize).floor(), b = map.project(bounds.getSouthEast(), z).divideBy(tileSize).floor();
  return { x0: a.x, x1: b.x, y0: a.y, y1: b.y };
}

// Builds the exact address Leaflet asks for, so a saved tile is found again later. Uses Leaflet internals
// (the WMS layer computes its URLs only once it is on a map), hence the loose typing here.
interface WmsInternals { _map: L.Map; _crs: L.CRS; _wmsVersion: number; _globalTileRange: L.Bounds; _tileZoom: number; wmsParams: L.WMSParams & { crs?: string } }
function wmsUrlMaker(map: L.Map, layer: L.TileLayer.WMS): (x: number, y: number, z: number) => string {
  const l = layer as unknown as WmsInternals;
  l._map = map; l._crs = map.options.crs!; l._wmsVersion = parseFloat(l.wmsParams.version ?? "1.3.0");
  l.wmsParams.crs = (l._crs as L.CRS & { code: string }).code; l._globalTileRange = L.bounds([0, 0], [0, 0]);
  return (x, y, z) => { l._tileZoom = z; const c = L.point(x, y) as L.Coords; c.z = z; return layer.getTileUrl(c); };
}

/** Tile URLs for the visible area, a few zoom levels deeper, capped at MAX_AREA_TILES. Null when even one level is too much. */
function planArea(map: L.Map): string[] | null {
  const base = LAYERS.mml!;
  const bounds = map.getBounds(), z0 = Math.max(6, Math.round(map.getZoom()));
  const wms = depthOn ? (depthLayers().getLayers() as L.TileLayer.WMS[]).map((l) => ({ url: wmsUrlMaker(map, l), minZoom: l.options.minZoom ?? 0 })) : [];
  for (let zMax = Math.min(base.native ?? base.max, z0 + 3); zMax >= z0; zMax--) {
    const jobs: string[] = [];
    for (let z = z0; z <= zMax; z++) {
      const r = tileRange(map, bounds, z, 256);
      for (let x = r.x0; x <= r.x1; x++) for (let y = r.y0; y <= r.y1; y++) jobs.push(L.Util.template(base.url, { x, y, z }));
      if (wms.length) {
        const w = tileRange(map, bounds, z, 512);
        for (const mk of wms) if (z >= mk.minZoom) for (let x = w.x0; x <= w.x1; x++) for (let y = w.y0; y <= w.y1; y++) jobs.push(mk.url(x, y, z));
      }
      if (jobs.length > MAX_AREA_TILES) break;
    }
    if (jobs.length <= MAX_AREA_TILES) return jobs;
  }
  return null;
}

function showBar(text: string, buttons: HTMLElement[]): void {
  $("#dl-text").textContent = text;
  $("#dl-btns").replaceChildren(...buttons);
  $("#v-map").classList.add("downloading");
  $("#map-download").setAttribute("aria-pressed", "true");
}
const hideBar = () => { $("#v-map").classList.remove("downloading"); $("#map-download").setAttribute("aria-pressed", "false"); };
const barButton = (label: string, onclick: () => void, primary = false) => el("button", { class: "btn" + (primary ? " primary" : ""), type: "button", onclick }, label);

let busy = false;
$("#map-download").addEventListener("click", () => {
  if (!bigMap || busy) return;
  const ok = () => barButton(t("ok"), hideBar, true);
  if (!LAYERS.mml) { showBar(t("offlineNeedKey"), [ok()]); return; }
  if (!window.caches) { showBar(t("offlineUnsupported"), [ok()]); return; }
  const jobs = planArea(bigMap);
  if (!jobs) { showBar(t("offlineTooBig"), [ok()]); return; }
  showBar(t("offlineAsk", jobs.length, fmtNum(Math.max(1, jobs.length * MB_PER_TILE), 0)),
    [barButton(t("cancel"), hideBar), barButton(t("download"), () => void downloadArea(jobs), true)]);
});

async function downloadArea(jobs: string[]): Promise<void> {
  busy = true;
  if (layerName !== "mml") setBaseLayer("mml");
  const cache = await caches.open(AREA_CACHE);
  let done = 0, failed = 0, next = 0;
  const progress = () => showBar(t("offlineProgress", done, jobs.length), []);
  progress();
  const worker = async () => {
    while (next < jobs.length) {
      const url = jobs[next++];
      try {
        if (!(await cache.match(url))) { const res = await fetch(url); if (!res.ok) throw new Error(String(res.status)); await cache.put(url, res); }
      } catch { failed++; }
      done++;
      if (done % 20 === 0) progress();
    }
  };
  try { await Promise.all([worker(), worker(), worker(), worker()]); }
  finally { busy = false; }
  navigator.storage?.persist?.().catch(() => {});
  showBar(failed ? t("offlineFailed", failed) : t("offlineDone"), [barButton(t("ok"), hideBar, true)]);
  void offlineInfo();
}

/** "N map tiles saved" under More, with a button to delete them. */
async function offlineInfo(): Promise<void> {
  const p = $("#offline-info"), btn = $("#offline-clear");
  if (!window.caches) { p.textContent = t("offlineUnsupported"); btn.hidden = true; return; }
  try {
    const n = (await (await caches.open(AREA_CACHE)).keys()).length;
    p.textContent = n ? t("offlineInfo", n, fmtNum(Math.max(1, n * MB_PER_TILE), 0)) : (LAYERS.mml ? t("offlineNone") : t("offlineNeedKey"));
    btn.hidden = !n;
  } catch { p.textContent = ""; }
}
twoTap($<HTMLButtonElement>("#offline-clear"), async () => { await caches.delete(AREA_CACHE); toast(t("offlineCleared")); void offlineInfo(); },
  () => t("offlineClear"), () => t("delConfirm"));

onRender(() => void offlineInfo());
